import React, { useMemo, useRef, useState } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  FormGroup,
  Label,
} from "reactstrap"

export default function GoogleReviewModal({
  open,
  toggle,
  url,
  presetText = "",
  openMode = "newtab",
}) {
  const [text, setText] = useState(presetText)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState("")
  const hiddenTaRef = useRef(null)
  const canOpen = useMemo(() => Boolean(url), [url])

  const close = () => (typeof toggle === "function" ? toggle(false) : null)

  const copyToClipboard = async value => {
    setError("")
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value)
        setCopied(true)
        return true
      }
    } catch {}
    try {
      if (hiddenTaRef.current) {
        hiddenTaRef.current.value = value
        hiddenTaRef.current.select()
        const ok = document.execCommand("copy")
        setCopied(ok)
        return ok
      }
    } catch {
      setError("Copy failed. Please copy manually.")
    }
    return false
  }

  const openGoogle = () => {
    if (!canOpen) return
    if (openMode === "popup") {
      const width = 600,
        height = 720
      const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2)
      const top =
        window.screenY + Math.max(0, (window.outerHeight - height) / 2)
      const features = [
        "popup=yes",
        "toolbar=no",
        "location=no",
        "status=no",
        "menubar=no",
        "scrollbars=yes",
        "resizable=yes",
        `width=${width}`,
        `height=${height}`,
        `left=${left}`,
        `top=${top}`,
      ].join(",")
      const w = window.open(url, "google_review", features)
      if (!w) window.open(url, "_blank", "noopener,noreferrer")
    } else if (openMode === "sametab") {
      window.location.assign(url)
    } else {
      window.open(url, "_blank", "noopener,noreferrer")
    }
  }

  const handleContinue = async () => {
    if (text) await copyToClipboard(text)
    openGoogle()
    close()
  }

  return (
    <Modal isOpen={open} toggle={close} size="md">
      <ModalHeader toggle={close}>Leave a quick Google review</ModalHeader>
      <ModalBody>
        <p className="mb-2">
          Edit or keep the text below; we'll copy it for you to paste on Google.
        </p>
        <FormGroup>
          <Label className="sr-only" htmlFor="presetText">
            Your review
          </Label>
          <Input
            id="presetText"
            type="textarea"
            rows={4}
            value={text}
            onChange={e => setText(e.target.value)}
          />
        </FormGroup>
        {copied && (
          <div className="text-success small">Copied to clipboard.</div>
        )}
        {error && <div className="text-danger small">{error}</div>}
        <textarea
          ref={hiddenTaRef}
          style={{ position: "absolute", left: -9999, top: -9999, opacity: 0 }}
          readOnly
        />
      </ModalBody>
      <ModalFooter className="d-flex justify-content-center gap-2">
        <Button color="secondary" onClick={close}>
          Maybe later
        </Button>
        <Button color="primary" onClick={handleContinue} disabled={!canOpen}>
          Copy & Continue to Google
        </Button>
      </ModalFooter>
    </Modal>
  )
}
